import {
  HiOutlineUser,
  HiOutlineBriefcase,
  HiOutlineCreditCard,
  HiOutlineUserGroup,
  HiOutlineCalendarDays,
  HiOutlineBanknotes,
  HiOutlineShieldCheck,
  HiOutlineDocumentText,
  HiOutlineHeart,
} from 'react-icons/hi2'

export const profileSections = [
  { id: 'details', label: 'Personal information', icon: HiOutlineUser, group: 'Personal' },
  { id: 'bank', label: 'Bank details', icon: HiOutlineCreditCard, group: 'Personal' },
  { id: 'super', label: 'Super', icon: HiOutlineBanknotes, group: 'Personal' },
  { id: 'tax', label: 'Tax details', icon: HiOutlineDocumentText, group: 'Personal' },
  { id: 'wellbeing', label: 'Wellbeing allowance', icon: HiOutlineHeart, group: 'Personal' },
  { id: 'leave', label: 'My leave', icon: HiOutlineCalendarDays, group: 'Personal' },
  { id: 'bills', label: 'My bills', icon: HiOutlineBanknotes, group: 'Personal' },
  { id: 'slips', label: 'Contractor slips', icon: HiOutlineDocumentText, group: 'Personal' },
  { id: 'employment', label: 'Employment details', icon: HiOutlineBriefcase, group: 'Employment' },
  { id: 'team', label: 'My team', icon: HiOutlineUserGroup, group: 'Employment' },
  { id: 'security', label: 'Security training', icon: HiOutlineShieldCheck, group: 'Employment' },
]

export function visibleProfileSections(isPhilippines: boolean, isViewingAs: boolean, isAustralian = false) {
  return profileSections.filter(
    (section) =>
      (section.id !== 'tax' || isAustralian || isPhilippines) &&
      (section.id !== 'super' || isAustralian) &&
      (section.id !== 'wellbeing' || isPhilippines) &&
      (section.id !== 'slips' || (isPhilippines && !isViewingAs))
  )
}

interface ProfileSearchItem {
  section: string
  label: string
  anchor?: string
  keywords: string
  country?: 'ph' | 'au'
}

const items: ProfileSearchItem[] = [
  {
    section: 'details',
    label: 'Full name',
    anchor: 'name',
    keywords: 'display preferred name personal information',
  },
  // One row on the page holds both legal names, so Quick find offers it once.
  { section: 'details', label: 'Legal name', anchor: 'legal-name', keywords: 'legal first name last name given family surname identity personal' },
  { section: 'details', label: 'Date of birth', anchor: 'date-of-birth', keywords: 'dob birthday born personal' },
  { section: 'details', label: 'Gender', anchor: 'gender', keywords: 'personal information' },
  {
    section: 'employment',
    label: 'Work email',
    anchor: 'work-email',
    keywords: 'contact email address',
  },
  {
    section: 'employment',
    label: 'Work mobile',
    anchor: 'work-mobile',
    keywords: 'contact aircall telephone phone number',
  },
  {
    section: 'details',
    label: 'Personal email',
    anchor: 'personal-email',
    keywords: 'private contact email address',
  },
  {
    section: 'details',
    label: 'Personal mobile',
    anchor: 'personal-mobile',
    keywords: 'contact telephone phone number',
  },
  {
    section: 'details',
    label: 'Residential address',
    anchor: 'address',
    keywords: 'home house unit apartment street city municipality state postcode postal barangay province metro manila zip code subdivision search address',
  },
  {
    section: 'details',
    label: 'Emergency contact',
    anchor: 'emergency',
    keywords: 'next of kin nok relationship phone',
  },
  {
    section: 'employment',
    label: 'Employment details',
    anchor: 'employment',
    keywords: 'company team location joined start date',
  },
  {
    section: 'bank',
    label: 'Bank details',
    keywords:
      'bank account name number bsb payroll salary payment',
  },
  { section: 'tax', label: 'Tax details', keywords: 'payroll tax taxation details' },
  { section: 'tax', label: 'Taxpayer Identification Number (TIN)', anchor: 'tax-tin', keywords: 'tin taxpayer bir tax identification number', country: 'ph' },
  { section: 'tax', label: 'Tax File Number (TFN)', anchor: 'tax-tfn', country: 'au', keywords: 'tax file number tfn' },
  { section: 'tax', label: 'Tax residency', anchor: 'tax-residency', country: 'au', keywords: 'australian foreign resident tax purposes' },
  { section: 'tax', label: 'Loan or student debt', anchor: 'tax-help-loan', country: 'au', keywords: 'hecs help student loan debt repayments yes no' },
  { section: 'super', label: 'Superannuation', keywords: 'super fund abn usi member account retirement payroll' },
  { section: 'team', label: 'My team', keywords: 'colleagues manager reporting line' },
  {
    section: 'leave',
    label: 'My leave',
    keywords: 'annual unpaid sick holiday holidays balance request time off absence overtime medical certificate attachment instructions history calendar',
  },
  {
    section: 'bills',
    label: 'My bills',
    keywords: 'expenses reimbursement receipts invoices claims approval status history',
  },
  {
    section: 'security',
    label: 'Security training',
    keywords: 'memos acknowledgement cyber awareness cyberhoot assignments completion completed training',
  },
  {
    section: 'wellbeing',
    label: 'Wellbeing allowance',
    keywords: 'benefits dependant dependent payment health well being wellness',
  },
  { section: 'slips', label: 'Contractor slips', keywords: 'payslips pay payment notices history' },
  { section: 'employment', label: 'Company', anchor: 'company', keywords: 'employer business organisation organization' },
  { section: 'employment', label: 'Team', anchor: 'team', keywords: 'division department' },
  { section: 'employment', label: 'Location', anchor: 'location', keywords: 'office country work workplace' },
  { section: 'employment', label: 'Start date', anchor: 'joined', keywords: 'joined joining employment service tenure anniversary years' },
  { section: 'bank', label: 'Account holder', anchor: 'bank-accountHolder', keywords: 'account name owner beneficiary' },
  { section: 'bank', label: 'BSB code', anchor: 'bank-bsb', keywords: 'branch routing number' },
  { section: 'bank', label: 'Account number', anchor: 'bank-accountNumber', keywords: 'payment salary' },
  { section: 'bank', label: 'Bank name', anchor: 'bank-bankName', keywords: 'financial institution' },
  { section: 'bank', label: 'Wise account email', anchor: 'bank-wiseEmail', keywords: 'contact email address', country: 'ph' },
  { section: 'bank', label: 'SWIFT / BIC', anchor: 'bank-swiftBic', keywords: 'international bank code', country: 'ph' },
  { section: 'bank', label: 'Wise proof of account', anchor: 'wise-proof', keywords: 'instructions upload replace pdf verification document recipient', country: 'ph' },
  { section: 'bank', label: 'Bank details history', anchor: 'bank-history', keywords: 'last updated change audit' },
  { section: 'super', label: 'Super fund', anchor: 'super-fund-row', keywords: 'superannuation abn usi retirement' },
  { section: 'super', label: 'Member number', anchor: 'super-member-number', keywords: 'superannuation membership account' },
  { section: 'wellbeing', label: 'Allowance tier', anchor: 'allowance-tier', keywords: 'current tiers upgrade milestone eligibility annual amount php benefit years service' },
  { section: 'wellbeing', label: 'Next payment', anchor: 'allowance-payment', keywords: 'allowance upgrade schedule pay run payroll month due date amount' },
  { section: 'wellbeing', label: 'Dependant status', anchor: 'allowance-dependant', keywords: 'dependent family child spouse eligibility' },
  { section: 'wellbeing', label: 'Payment history', anchor: 'allowance-history', keywords: 'previous received paid allowance' },
  { section: 'team', label: 'Line manager', anchor: 'line-manager', keywords: 'supervisor leader reporting contact email phone' },
  { section: 'team', label: 'Direct reports', anchor: 'direct-reports', keywords: 'staff employees members colleagues co manager' },
  { section: 'security', label: 'Compliance', anchor: 'compliance', keywords: 'compliant outstanding assignments completion pending verification' },
  { section: 'security', label: 'Security score', anchor: 'security-score', keywords: 'cyber rating training' },
]

function searchableText(value: string) {
  return value.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()
}

// Navigation metadata only: no private field values or extra API reads.
export function findProfileItems(query: string, visibleSectionIds: string[], isPhilippines = false) {
  const words = searchableText(query).split(/\s+/).filter(Boolean)
  return items.filter(item => {
    if (!visibleSectionIds.includes(item.section)) return false
    if (item.country && (item.country === 'ph') !== isPhilippines) return false
    const sectionLabel = profileSections.find(section => section.id === item.section)?.label ?? ''
    const text = searchableText(`${sectionLabel} ${item.label} ${item.keywords}`)
    return words.every(word => text.includes(word))
  })
}
